import React from 'react';
import { Alert, Box, List, PageContent, Stack, Text } from 'bumbag';
import { Form, Formik, FormikProps } from 'formik';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import * as Loads from 'react-loads-next';

import { ErrorMessage } from '@medipass/react-core';
import { LabelWrapper } from '@medipass/react-core';
import { PrivacyStatement } from '@medipass/react-core';
import { FUTURE_TYPES, TRANSACTION_PAYMENT_OPTIONS } from '@medipass/utils/constants';

import { ApproveRejectButtons } from 'components/ApproveRejectButtons';
import ApproveDeclaration from 'legacy/components/checkout/ApproveDeclaration';
import FinishPage from 'legacy/components/checkout/layout/FinishPage';
import ApproveElectronicClaimsLodgement from './ApproveElectronicClaimsLodgement';
const INITIAL_VALUES = {
  isCreatingAccount: true,
  cardNumber: '',
  electronicClaimLodgement: false,
  expiry: '',
  cvc: '',
  location: undefined,
  firstName: '',
  lastName: '',
  dob: '',
  email: '',
  mobile: '',
  receiptEmail: '',
  bankAccountName: '',
  bankAccountNumber: '',
  bankBSB: '',
  bankAccountCheckBox: 'false',
};

// @ts-expect-error TS(7006): Parameter 'errors' implicitly has an 'any' type.
const getHumanizedFormErrors = errors => {
  const keys = Object.keys(errors);
  // @ts-expect-error TS(7034): Variable 'humanizedErrors' implicitly has type 'an... Remove this comment to see the full error message
  let humanizedErrors = [];

  if (keys.includes('cardNumber') || keys.includes('expiry') || keys.includes('cvc')) {
    // @ts-expect-error TS(7005): Variable 'humanizedErrors' implicitly has an 'any[... Remove this comment to see the full error message
    humanizedErrors = [...humanizedErrors, 'Payment card details'];
  }

  if (
    keys.includes('firstName') ||
    keys.includes('lastName') ||
    keys.includes('dob') ||
    keys.includes('email') ||
    keys.includes('mobile')
  ) {
    // @ts-expect-error TS(7005): Variable 'humanizedErrors' implicitly has an 'any[... Remove this comment to see the full error message
    humanizedErrors = [...humanizedErrors, 'Personal details'];
  }

  if (keys.includes('receiptEmail')) {
    // @ts-expect-error TS(7005): Variable 'humanizedErrors' implicitly has an 'any[... Remove this comment to see the full error message
    humanizedErrors = [...humanizedErrors, 'Receipt email'];
  }

  if (keys.includes('electronicClaimLodgement')) {
    // @ts-expect-error TS(7005): Variable 'humanizedErrors' implicitly has an 'any[... Remove this comment to see the full error message
    humanizedErrors = [...humanizedErrors, 'Electronic lodgement of claim authorisation'];
  }

  // @ts-expect-error TS(7005): Variable 'humanizedErrors' implicitly has an 'any[... Remove this comment to see the full error message
  return humanizedErrors;
};

interface ApproveFormProps {
  children: ((formik: FormikProps<any>) => React.ReactNode) | React.ReactNode;
  enableApplePay?: boolean;
  enableGooglePay?: boolean;
  enableReinitialize?: boolean;
  initialValues?: Record<string, any>;
  isCheckout?: boolean;
  onApplePayError?: (...args: Array<any>) => any;
  onClickReject: (...args: Array<any>) => any;
  onSubmit: (...args: Array<any>) => any;
  showDeclaration: boolean;
  showElectronicLodgementDeclaration: boolean;
  showReject?: boolean;
  transaction?: Record<string, any>;
  wrapper?: (...args: Array<any>) => any;
  paymentOption: any;
  selectedPaymentMethod?: any;
  isCreatingNewPaymentMethod?: boolean;
}

export default function ApproveForm({
  children = <div />,
  paymentOption,
  enableApplePay = true,
  enableGooglePay = true,
  enableReinitialize,
  initialValues = {},
  isCheckout = true,
  onApplePayError,
  onClickReject,
  onSubmit,
  selectedPaymentMethod,
  showDeclaration,
  showElectronicLodgementDeclaration,
  showReject = true,
  transaction = {},
  isCreatingNewPaymentMethod,
  wrapper = children => children,
}: ApproveFormProps) {
  const submitLoader = Loads.useDeferredLoads(onSubmit);
  const submitExternalPaymentLoader = Loads.useDeferredLoads(onSubmit);
  const rejectLoader = Loads.useDeferredLoads(onClickReject);
  const handleSubmitExternalPayment = React.useCallback(
    // @ts-expect-error TS(7006): Parameter 'formik' implicitly has an 'any' type.
    formik => data => {
      submitExternalPaymentLoader.load({ ...data, rememberCardDetails: _get(formik, 'values.rememberCardDetails') });
  }, [] //eslint-disable-line
  );
  let amountToPayNow: any;
  let totalToPayLabel = 'Total to pay';

  if (transaction.futureType === FUTURE_TYPES.INSTALMENT_PLAN) {
    const numberOfInstalments = _get(transaction, 'instalmentPlan.numberOfInstalments');

    totalToPayLabel = `Total to pay over ${numberOfInstalments} instalments`;
    amountToPayNow = _get(transaction, 'instalmentPlan.paymentSchedule[0].amountString');
  }

  const isCardExpired = (paymentMethod: any) => {
    if (_isEmpty(paymentMethod)) return false;
    const expiryDate = new Date(paymentMethod?.expiryYear, paymentMethod?.expiryMonth - 1, 1);
    return expiryDate < new Date();
  };

  const isSelectedPaymentExpired = isCardExpired(selectedPaymentMethod);

  return (
    <Formik
      enableReinitialize={enableReinitialize}
      initialValues={{ ...INITIAL_VALUES, ...initialValues }}
      onSubmit={submitLoader.load}
    >
      {formik => (
        <Form noValidate>
          {submitLoader.isPending ||
          submitExternalPaymentLoader.isPending ||
          submitLoader.isResolved ||
          submitExternalPaymentLoader.isResolved ? (
            <FinishPage state="loading" />
          ) : (
            <Box>
              {wrapper(
                <React.Fragment>
                  {(submitLoader.isRejected || submitExternalPaymentLoader.isRejected) && (
                    <PageContent breakpoint="checkout">
                      <Alert
                        variant="tint"
                        hasIcon
                        type="danger"
                        style={{
                          width: '100%',
                        }}
                      >
                        <ErrorMessage
                          description="Unable to approve your payment. Please try again."
                          // @ts-expect-error TS(2322): Type 'unknown' is not assignable to type 'Record<s... Remove this comment to see the full error message
                          err={submitLoader.error || submitExternalPaymentLoader.error}
                        />
                      </Alert>
                    </PageContent>
                  )}
                  {typeof children === 'function' ? children(formik) : children}
                  <PageContent breakpoint="checkout">
                    <Stack spacing="major-1">
                      {!_isEmpty(formik.errors) && formik.submitCount > 0 && (
                        <Alert hasIcon variant="tint" type="danger">
                          It looks like there are errors in the form you filled out.
                          {getHumanizedFormErrors(formik.errors).length > 0 && (
                            <Text>
                              {' '}
                              Please review the following before submitting:
                              <List listStyleType="disc" listStylePosition="inside">
                                {getHumanizedFormErrors(formik.errors).map(error => (
                                  <List.Item key={error}>{error}</List.Item>
                                ))}
                              </List>
                            </Text>
                          )}
                        </Alert>
                      )}
                      {showDeclaration && <ApproveDeclaration className={null} />}
                      {showElectronicLodgementDeclaration && <ApproveElectronicClaimsLodgement />}
                      {amountToPayNow && (
                        <LabelWrapper
                          label="Amount to pay now"
                          descriptionProps={{
                            whiteSpace: 'nowrap',
                          }}
                          valueProps={{
                            textAlign: 'right',
                          }}
                          marginBottom="-1rem"
                        >
                          {amountToPayNow}
                        </LabelWrapper>
                      )}
                      {_get(transaction, 'amountBalanceString') && (
                        <LabelWrapper
                          label={totalToPayLabel}
                          descriptionProps={{
                            whiteSpace: 'nowrap',
                          }}
                          valueProps={{
                            textAlign: 'right',
                          }}
                        >
                          {_get(transaction, 'amountBalanceString')}
                        </LabelWrapper>
                      )}
                      <ApproveRejectButtons
                        isDisabled={
                          paymentOption !== TRANSACTION_PAYMENT_OPTIONS.PAY_IN_PERSON &&
                          ((_isEmpty(selectedPaymentMethod) && !isCreatingNewPaymentMethod) ||
                            isSelectedPaymentExpired) &&
                          transaction?.amountBalance > 0
                        }
                        enableApplePay={enableApplePay}
                        enableGooglePay={enableGooglePay}
                        isLoading={submitLoader.isPending || rejectLoader.isPending}
                        onApplePayError={onApplePayError}
                        onApplePaySuccess={handleSubmitExternalPayment(formik)}
                        onClickExternalPayment={handleSubmitExternalPayment(formik)}
                        onClickReject={rejectLoader.load}
                        showReject={showReject}
                        transaction={transaction}
                      />
                      {rejectLoader.isRejected && (
                        <Alert
                          variant="tint"
                          hasIcon
                          type="danger"
                          style={{
                            width: '100%',
                          }}
                        >
                          <ErrorMessage
                            description="Unable to reject this payment. Please try again."
                            // @ts-expect-error TS(2322): Type 'unknown' is not assignable to type 'Record<s... Remove this comment to see the full error message
                            err={rejectLoader.error}
                          />
                        </Alert>
                      )}
                      {isCheckout && <PrivacyStatement />}
                    </Stack>
                  </PageContent>
                </React.Fragment>,
              )}
            </Box>
          )}
        </Form>
      )}
    </Formik>
  );
}
